import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Paper, Typography, Button, Alert, CircularProgress, Link } from '@mui/material';
import api from '../services/api';

// Public-ish landing page for company-membership invitations. Anyone can
// load /invite/:token to see what's behind it (company name + role). To
// actually accept, the recipient must be authenticated as the invited
// email. Login/signup happens elsewhere — this page just routes them.
function InviteLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { token: jwt, userInfo } = useSelector((s) => s.auth);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/users/invitations/${token}`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          const status = e.response?.status;
          if (status === 410) setError('This invitation is no longer valid (revoked, accepted, or expired).');
          else if (status === 404) setError('Invitation not found. It may have been revoked.');
          else setError(e.response?.data?.detail || 'Could not load invitation.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await api.post(`/api/users/invitations/${token}/accept`);
      setAccepted(true);
      // Drop into the company picker so they can pick the new
      // membership and land in the right context.
      setTimeout(() => navigate('/pick-company'), 1200);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not accept invitation.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 480, width: '100%' }}>
        <Typography variant="h5" gutterBottom>You've been invited</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {preview && !accepted && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You've been invited to join <strong>{preview.company_name}</strong> as
              {' '}<strong>{preview.role.replace('_', ' ')}</strong>.
            </Typography>

            {!jwt ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Sign in or create an account with the invited email address to accept.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    component={RouterLink}
                    to={`/login?returnTo=${encodeURIComponent(`/invite/${token}`)}`}
                    variant="contained"
                  >
                    Sign in
                  </Button>
                  <Button
                    component={RouterLink}
                    to={`/register?invite=${encodeURIComponent(token)}`}
                    variant="outlined"
                  >
                    Create account
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Signed in as <strong>{userInfo?.email}</strong>. Make sure this matches
                  the invited address — invitations only accept on a matching email.
                </Typography>
                <Button variant="contained" onClick={onAccept} disabled={accepting}>
                  {accepting ? 'Accepting…' : 'Accept invitation'}
                </Button>
                {' '}
                <Link component={RouterLink} to="/profile" sx={{ ml: 2 }}>
                  Wrong account?
                </Link>
              </>
            )}
          </>
        )}

        {accepted && (
          <Alert severity="success">
            Invitation accepted. Redirecting…
          </Alert>
        )}
      </Paper>
    </Box>
  );
}

export default InviteLanding;
